import { Accordion, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import styles from './AccessorialsSection.module.css';
import { freightFormMessages } from './freight-form-messages';

import type { LtlAdditionalServices } from '../../../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

type AccessorialsSectionProps = {
  value: LtlAdditionalServices;
  onChange: (next: LtlAdditionalServices) => void;
};

type AccessorialField = {
  key: keyof LtlAdditionalServices;
  label: MessageDescriptor;
};

const PICKUP_ACCESSORIALS: AccessorialField[] = [
  { key: 'isInsidePickup', label: freightFormMessages.accessorialInsidePickup },
  { key: 'isConstructionSitePickup', label: freightFormMessages.accessorialConstructionSitePickup },
  { key: 'isLiftgatePickup', label: freightFormMessages.accessorialLiftgatePickup },
  { key: 'isNotifyBeforePickup', label: freightFormMessages.accessorialNotifyBeforePickup },
  { key: 'isResidentialPickup', label: freightFormMessages.accessorialResidentialPickup },
  { key: 'isDockLevelPickup', label: freightFormMessages.accessorialDropShipmentAtTerminal },
  { key: 'isGroceryConsolidationPickup', label: freightFormMessages.accessorialGroceryConsolidationPickup },
  { key: 'isLimitedAccessPickup', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowPickup', label: freightFormMessages.accessorialTradeshowPickup },
];

const DELIVERY_ACCESSORIALS: AccessorialField[] = [
  { key: 'isInsideDelivery', label: freightFormMessages.accessorialInsideDelivery },
  { key: 'isConstructionSiteDelivery', label: freightFormMessages.accessorialConstructionSiteDelivery },
  { key: 'isLiftgateDelivery', label: freightFormMessages.accessorialLiftgateDelivery },
  { key: 'isNotifyBeforeDelivery', label: freightFormMessages.accessorialNotifyBeforeDelivery },
  { key: 'isResidentialDelivery', label: freightFormMessages.accessorialResidentialDelivery },
  { key: 'isDockLevelDelivery', label: freightFormMessages.accessorialHoldShipmentAtTerminal },
  { key: 'isGroceryConsolidationDelivery', label: freightFormMessages.accessorialGroceryConsolidationDelivery },
  { key: 'isAppointmentDelivery', label: freightFormMessages.accessorialAppointmentDelivery },
  { key: 'isLimitedAccessDelivery', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowDelivery', label: freightFormMessages.accessorialTradeshowDelivery },
];

export const AccessorialsSection = ({ value, onChange }: AccessorialsSectionProps) => {
  const intl = useIntl();

  const renderColumn = (fields: AccessorialField[]) => (
    <Stack gap='xs'>
      {fields.map((field) => (
        <Checkbox
          checked={value[field.key]}
          key={field.key}
          label={intl.formatMessage(field.label)}
          onChange={(e) => onChange({ ...value, [field.key]: e.currentTarget.checked })}
        />
      ))}
    </Stack>
  );

  return (
    <Accordion
      classNames={{ control: styles.control, label: styles.label, panel: styles.panel, root: styles.root }}
      data-testid='accessorials-section'
      variant='contained'
    >
      <Accordion.Item value='accessorials'>
        <Accordion.Control>{intl.formatMessage(freightFormMessages.accessorialsTitle)}</Accordion.Control>
        <Accordion.Panel>
          <Group align='flex-start' grow>
            <Stack gap='sm'>
              <Text fw={600} size='sm'>
                {intl.formatMessage(freightFormMessages.pickupServicesHeader)}
              </Text>
              {renderColumn(PICKUP_ACCESSORIALS)}
            </Stack>
            <Stack className={styles.deliveryColumn} gap='sm'>
              <Text fw={600} size='sm'>
                {intl.formatMessage(freightFormMessages.deliveryServicesHeader)}
              </Text>
              {renderColumn(DELIVERY_ACCESSORIALS)}
            </Stack>
          </Group>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
